import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState } from "react";

const ThemeContext = createContext({ theme: "dark", mode: "dark", toggle: () => {}, setMode: (_mode) => {} });
const validMode = (value) => ["dark", "light", "system"].includes(value);
function initialMode() {
  try { const saved = localStorage.getItem("mb-theme"); return validMode(saved) ? saved : "system"; }
  catch { return "system"; }
}
function systemIsDark() {
  return typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)").matches : true;
}
export const ThemeProvider = ({ children }) => {
  const [mode, updateMode] = useState(initialMode);
  const [systemDark, setSystemDark] = useState(systemIsDark);
  const theme = mode === "system" ? (systemDark ? "dark" : "light") : mode;
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const changed = () => setSystemDark(media.matches);
    changed();
    media.addEventListener?.("change", changed);
    return () => media.removeEventListener?.("change", changed);
  }, []);
  useLayoutEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
    try { localStorage.setItem("mb-theme", mode); } catch { /* The view still works if storage is unavailable. */ }
  }, [mode, theme]);
  const setMode = useCallback((next) => { if (validMode(next)) updateMode(next); }, []);
  const toggle = useCallback(() => updateMode(theme === "dark" ? "light" : "dark"), [theme]);
  return <ThemeContext.Provider value={{ theme, mode, toggle, setMode }}>{children}</ThemeContext.Provider>;
};
export const useThemeMode = () => useContext(ThemeContext);
